import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Container, Form, Button, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBriefcase, FiFileText } from 'react-icons/fi';
import { API_BASE } from '../lib/apiBase';

/**
 * PublishJobs lets admin/office users create job listings. Office submissions
 * are stored as pending until an admin approves them via the Awaiting Approval
 * queue.
 */
const PublishJobs = () => {
  const userRole = sessionStorage.getItem('userRole');
  const [formData, setFormData] = useState({
    title: '',
    type: 'Full-time',
    fte: '',
    closingDate: '',
    description: '',
    qualifications: '',
    salaryFrom: '',
    salaryTo: '',
    salaryCurrency: 'AUD',
    salaryPeriod: 'year',
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const parsed = useMemo(() => {
    // Convert newline-delimited text areas into arrays for preview lists
    return {
      qualifications: (formData.qualifications || '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean),
    };
  }, [formData.qualifications]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic validation
    if (!formData.title.trim()) return setError('Job title is required.');
    if (!formData.description.trim()) return setError('Job description is required.');
    if (formData.salaryFrom && isNaN(Number(formData.salaryFrom))) return setError('Salary (from) must be a number.');
    if (formData.salaryTo && isNaN(Number(formData.salaryTo))) return setError('Salary (to) must be a number.');

    try {
      const role = userRole || 'admin';
      const payload = {
        ...formData,
        qualifications: parsed.qualifications,
      };

      const response = await axios.post(`${API_BASE}/jobs`, payload, {
        headers: { 'Content-Type': 'application/json', 'x-user-role': role },
      });

      if (response.status === 202 || role === 'office') {
        setSuccess('Job submitted for approval. An admin will publish it once reviewed.');
      } else {
        setSuccess('Job published successfully.');
      }

      setFormData({
        title: '',
        type: 'Full-time',
        fte: '',
        closingDate: '',
        description: '',
        qualifications: '',
        salaryFrom: '',
        salaryTo: '',
        salaryCurrency: 'AUD',
        salaryPeriod: 'year',
      });
    } catch (err) {
      console.error('Error publishing job:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  if (!userRole) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Please log in as an admin or office user to publish job listings.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button variant="link" className="text-decoration-none" onClick={() => navigate(-1)}>
          <FiArrowLeft className="me-1" /> Back
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3">
          <h4 className="mb-0 d-flex align-items-center">
            <FiBriefcase className="text-warning me-2" /> Publish New Job
          </h4>
        </Card.Header>
        <Card.Body className="p-4">
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={7}>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Mathematics Teacher"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Employment Type</Form.Label>
                      <Form.Select name="type" value={formData.type} onChange={handleChange}>
                        <option>Full-time</option>
                        <option>Part-time</option>
                        <option>Casual</option>
                        <option>Contract</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>FTE / Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="fte"
                    value={formData.fte}
                    onChange={handleChange}
                    placeholder="e.g., 1.0 (full-time) or 0.6"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Closing Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="closingDate"
                    value={formData.closingDate}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Row>
                  <Col md={7}>
                    <Form.Group className="mb-3">
                      <Form.Label>Salary Range</Form.Label>
                      <InputGroup>
                        <Form.Select name="salaryCurrency" value={formData.salaryCurrency} onChange={handleChange} style={{ maxWidth: 110 }}>
                          <option value="AUD">AUD</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </Form.Select>
                        <Form.Control
                          type="text"
                          name="salaryFrom"
                          value={formData.salaryFrom}
                          onChange={handleChange}
                          placeholder="From"
                        />
                        <Form.Control
                          type="text"
                          name="salaryTo"
                          value={formData.salaryTo}
                          onChange={handleChange}
                          placeholder="To"
                        />
                        <Form.Select name="salaryPeriod" value={formData.salaryPeriod} onChange={handleChange} style={{ maxWidth: 120 }}>
                          <option value="year">/year</option>
                          <option value="hour">/hour</option>
                        </Form.Select>
                      </InputGroup>
                      <Form.Text className="text-muted">Optional. Leave blank if not disclosed.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    rows={6}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Role summary, responsibilities, qualifications..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Qualifications & Experience</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="qualifications"
                    rows={5}
                    value={formData.qualifications}
                    onChange={handleChange}
                    placeholder={"One per line\n• Teaching qualification\n• VIT registration"}
                  />
                </Form.Group>

                

                <div className="d-grid">
                  <Button type="submit" variant="warning">{formData.status === 'draft' ? 'Save Draft' : 'Publish Job'}</Button>
                </div>
              </Form>
            </Col>

            <Col md={5} className="mt-4 mt-md-0">
              <div className="p-3 border rounded-3 bg-light h-100">
                <h6 className="mb-3 d-flex align-items-center">
                  <FiFileText className="me-2" /> Preview
                </h6>
                <div>
                  <div className="fw-bold">{formData.title || 'Job title'}</div>
                  <div className="text-muted mb-1">{formData.type}{formData.fte ? ` • ${formData.fte}` : ''}</div>
                  <div className="text-muted mb-2">{formData.closingDate ? `Closes: ${formData.closingDate}` : 'No closing date set'}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{formData.description || 'Job description...'}</div>
                  {(formData.salaryFrom || formData.salaryTo) && (
                    <div className="mt-2">
                      Salary: {formData.salaryCurrency} {formData.salaryFrom || '?'} - {formData.salaryTo || '?'} / {formData.salaryPeriod}
                    </div>
                  )}
                  {parsed.qualifications.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold">Qualifications & Experience</div>
                      <ul className="mb-0">
                        {parsed.qualifications.map((q, i) => <li key={i}>{q}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mt-2 text-muted small">
                    Applicants will submit via the Avenue School careers portal.
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PublishJobs;
